import { DashCard } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/DashCard/DashCard';
import {
	CHIP_THEME,
	Chips as ChipsV2,
	COLOR,
} from '@dealstack/frontend/shared/components/Chips/Chips.v2';
import { usePermissionRequest } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper';
import { Button as ButtonV2 } from '@dealstack/frontend/shared/components/fundament/Button/Button.v2';
import { TEST_ELEMENT_IDS } from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import {
	useTranslationCommonUi,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	PermissionRole,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions';
import { defaultToEmptyString } from '@dealstack/shared/lib/format/string';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PredefinedIcons } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.types';
import {
	AcceptanceStatus,
	DocumentWorkflowTemplateVisibleIn,
	KYCWorkflowStatus,
	KYCFileType,
} from '@dealstack/shared/lib/generated';
import { useNavigateToKycPage } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useNavigateToKycPage';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns';
import { useManageInvestorDetailsController } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetails/useManageInvestorDetailsController';
import { MIPManageInvestorsV2Context } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/MIPManageInvestorsV2Context';
import { useKycWorkflowKycSetting } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useKycSetting';
import { useQuery } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { getInvestorFormsForReview } from '@dealstack/frontend/shared/dataExchange/services/mip-manage.service';

interface Props {
	className?: string;
	kycStatus: KYCWorkflowStatus;
	kycWorkflowId?: string | null;
	isPiv: boolean;
}

export const DashcardKYC = ({ className, kycStatus, kycWorkflowId, isPiv }: Props) => {
	const { mipId, investorId, dealId } = useParams();
	const tMipManage = useTranslationMipManage();
	const tCommonUi = useTranslationCommonUi();
	const { navigateToKycPage } = useNavigateToKycPage();

	const manageInvestorDetailsController = useManageInvestorDetailsController();
	const mipManageController = useContext(MIPManageInvestorsV2Context);

	const { data: kycSetting } = useKycWorkflowKycSetting(kycWorkflowId ?? '');

	// Attached forms for THIS workflow + their submission status
	const { data: formSubmissionsData } = useQuery({
		queryKey: [
			API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_KYC_FORMS_KYC_WORKFLOW_KYCWORKFLOWID_INVESTOR_FORMS_GET,
			dealId,
			mipId,
			kycWorkflowId,
		],
		queryFn: () =>
			getInvestorFormsForReview({
				dealId: dealId ?? '',
				programmeId: mipId ?? '',
				kycWorkflowId: kycWorkflowId ?? '',
			}),
		enabled: !!dealId && !!mipId && !!kycWorkflowId,
	});

	// Only forms matching the investor's current audience are actually required (PIV → vehicle, else
	// individual) — cross-audience forms must not inflate the counts. Mirrors the status trigger + review page.
	const audienceForms = useMemo(
		() =>
			(formSubmissionsData?.payload ?? []).filter((form) =>
				form.visibleIn.includes(
					isPiv
						? DocumentWorkflowTemplateVisibleIn.KYC_VEHICLE
						: DocumentWorkflowTemplateVisibleIn.KYC_INDIVIDUAL
				)
			),
		[formSubmissionsData?.payload, isPiv]
	);
	const attachedFormsCount = audienceForms.length;

	const formCounts = useMemo(() => {
		let supplied = 0;
		let approved = 0;
		let rejected = 0;
		for (const form of audienceForms) {
			if (form.status === null) {
				continue;
			}
			supplied += 1;
			if (form.status === AcceptanceStatus.APPROVED) {
				approved += 1;
			}
			if (
				form.status === AcceptanceStatus.REJECTED ||
				form.status === AcceptanceStatus.REREQUESTED
			) {
				rejected += 1;
			}
		}
		return { supplied, approved, rejected };
	}, [audienceForms]);

	const declarations = manageInvestorDetailsController.investorDetailsData?.declarations;
	const kycFiles = manageInvestorDetailsController.investorDetailsData?.kycFiles;
	const [isReviewOpen, setIsReviewOpen] = useState(false);

	const isMorePivDocsRequested = !!mipManageController.investors?.some(
		(investor) =>
			investor.id === manageInvestorDetailsController.investorId &&
			investor?.processSettings?.areMorePivDocsRequested
	);

	useEffect(() => {
		if (isReviewOpen && !manageInvestorDetailsController.showReviewModal) {
			manageInvestorDetailsController.refreshMIPInvestorDetailsData();
			mipManageController.refreshData();
			setIsReviewOpen(false);
		}
	}, [isReviewOpen, manageInvestorDetailsController.showReviewModal]);

	const counts = useMemo(() => {
		if (!kycFiles || !kycSetting) {
			return { supplied: 0, approved: 0, rejected: 0 };
		}

		const isProofOfIdentityRequired = kycSetting.isProofOfIdentityRequired;
		const isProofOfAddressRequired = kycSetting.isProofOfAddressRequired;
		const isProofOfInvestmentVehicleRequired = kycSetting.isProofOfInvestmentVehicleRequired;

		// Proof files only — forms are DocumentWorkflow-backed (counted via formCounts); legacy KYCFile
		// FORM rows must not be counted here. Required-type filtering is unchanged.
		const filteredKycFiles = kycFiles.filter((file) => {
			if (file.type === KYCFileType.FORM) {
				return false;
			}
			if (file.type === KYCFileType.PROOF_OF_IDENTITY && !isProofOfIdentityRequired) {
				return false;
			}
			if (file.type === KYCFileType.PROOF_OF_ADDRESS && !isProofOfAddressRequired) {
				return false;
			}
			if (
				file.type === KYCFileType.INVESTMENT_VEHICLE &&
				(!isProofOfInvestmentVehicleRequired || !isPiv)
			) {
				return false;
			}
			return true;
		});

		const isDeclarationProvided = declarations?.some((declaration) => declaration.checked);
		const isDeclarationStatusAccepted = declarations?.some(
			(declaration) => declaration.status === AcceptanceStatus.APPROVED
		);
		const isDeclarationStatusRejected = declarations?.some(
			(declaration) =>
				declaration.status === AcceptanceStatus.REJECTED ||
				declaration.status === AcceptanceStatus.REREQUESTED
		);
		// We count all declarations as 1 entity here
		const numberOfDeclarationFiles = 1;

		const numberOfSuppliedFiles = isDeclarationProvided
			? filteredKycFiles.length + numberOfDeclarationFiles
			: filteredKycFiles.length;
		const numberOfAcceptedKYCFiles =
			filteredKycFiles.filter((file) => file.status === AcceptanceStatus.APPROVED)?.length ??
			0;
		const numberOfRejectedKYCFiles =
			filteredKycFiles.filter(
				(file) =>
					file.status === AcceptanceStatus.REJECTED ||
					file.status === AcceptanceStatus.REREQUESTED
			)?.length ?? 0;

		const numberOfApprovedFiles = isDeclarationStatusAccepted
			? numberOfAcceptedKYCFiles + numberOfDeclarationFiles
			: numberOfAcceptedKYCFiles;
		const numberOfRejectedFiles = isDeclarationStatusRejected
			? numberOfRejectedKYCFiles + numberOfDeclarationFiles
			: numberOfRejectedKYCFiles;

		return {
			supplied: numberOfSuppliedFiles + formCounts.supplied,
			approved: numberOfApprovedFiles + formCounts.approved,
			rejected: numberOfRejectedFiles + formCounts.rejected,
		};
	}, [kycFiles, declarations, kycSetting, isPiv, formCounts]);

	const suppliedFilesTextValue = counts.supplied.toString();
	const approvedFilesTextValue = counts.approved.toString();
	const rejectedFilesTextValue = counts.rejected.toString();

	// "Requested" = ALL required KYC items (like the main KYC view), not just forms. We compute the total
	// from THIS workflow's settings so it respects the per-investment override + audience: required proofs
	// (+ vehicle if PIV) + declarations (one entity, only if any group is required, since declarations are
	// optional) + the attached, audience-matching forms.
	const anyDeclarationRequired =
		!!kycSetting?.isBeneficialOwnershipRequired ||
		!!kycSetting?.isSourceOfFundsRequired ||
		!!kycSetting?.isPepRequired;
	const nonFormsRequestedAmount =
		(kycSetting?.isProofOfIdentityRequired ? 1 : 0) +
		(kycSetting?.isProofOfAddressRequired ? 1 : 0) +
		(isPiv && kycSetting?.isProofOfInvestmentVehicleRequired ? 1 : 0) +
		(anyDeclarationRequired ? 1 : 0);

	const requestedItemsAmount = nonFormsRequestedAmount + attachedFormsCount;

	const requestedFilesValue =
		requestedItemsAmount === 0 ? LONG_DASH : requestedItemsAmount.toString();

	const prepareContentArray = (): { key: string; value: string }[] => {
		return [
			{
				key: tMipManage('investors.details.dashcards.kyc.requested'),
				value: requestedFilesValue,
			},
			{
				key: tMipManage('investors.details.dashcards.kyc.supplied'),
				value: suppliedFilesTextValue,
			},
			{
				key: tMipManage('investors.details.dashcards.kyc.approved'),
				value: approvedFilesTextValue,
			},
			{
				key: tMipManage('investors.details.dashcards.kyc.rejected'),
				value: rejectedFilesTextValue,
			},
		];
	};

	const contentArray =
		kycStatus === KYCWorkflowStatus.APPROVED
			? [
					{
						key: tMipManage('investors.dashcards.kyc.items_approved'),
						value: approvedFilesTextValue,
					},
				]
			: prepareContentArray();

	const onKycReviewClick = () => {
		navigateToKycPage({
			investorId: investorId ?? '',
			selectedInvestor: investorId ?? '',
			kycWorkflowId: kycWorkflowId ?? undefined,
		});
	};
	const isStatusComplete = kycStatus === KYCWorkflowStatus.APPROVED;
	const requestNotSendOut = kycStatus === KYCWorkflowStatus.DEFINITION_COMPLETE;

	const prefixIcon: PredefinedIcons = isMorePivDocsRequested ? 'send-back-v3' : 'checkmark';
	const textKey: string = isMorePivDocsRequested ? 'chips.more_requested' : 'chips.complete';

	const areFilesToBeReviewed = Number(suppliedFilesTextValue) > 0;
	const showAwaitingInvestorPill =
		kycStatus === KYCWorkflowStatus.IN_PROGRESS && !areFilesToBeReviewed;
	const hideReviewButton = isStatusComplete || requestNotSendOut || !areFilesToBeReviewed;
	const { hasPermission: hasKycAdminRole } = usePermissionRequest({
		resourceType: ResourceType.PROGRAMME,
		resourceId: defaultToEmptyString(mipId),
		permissionRole: PermissionRole.KYC_ADMIN,
	});

	const middleRowButtonContent: React.ReactNode = (
		<div className="flex items-center gap-2">
			{!hideReviewButton &&
				(hasKycAdminRole ? (
					<ButtonV2
						data-testid={
							TEST_ELEMENT_IDS.MIP_MANAGE_INVESTORS_DETAILS_DASHCARD_KYC_REVIEW_BUTTON
						}
						size="xs"
						variant="primary"
						icon="view-v3"
						leftIcon
						buttonTheme="light"
						onClick={onKycReviewClick}
						className={className}
					>
						{tMipManage('investors.details.dashcards.kyc.review_button')}
					</ButtonV2>
				) : (
					<ChipsV2
						size="s"
						color={COLOR.PEACH}
						chipTheme={CHIP_THEME.DARK}
						prefixIcon="hourglass-v3"
						className={className}
					>
						{tMipManage('investors.details.dashcards.kyc.ready_be_reviewed')}
					</ChipsV2>
				))}

			{showAwaitingInvestorPill && (
				<ChipsV2
					size="s"
					color={COLOR.PEACH}
					chipTheme={CHIP_THEME.DARK}
					prefixIcon="hourglass-v3"
					className={className}
				>
					{tMipManage('investors.details.dashcards.kyc.in_progress')}
				</ChipsV2>
			)}
		</div>
	);

	const middleRowStatusContent = isStatusComplete ? (
		<ChipsV2
			prefixIcon={prefixIcon}
			size="s"
			chipTheme={CHIP_THEME.DARK}
			color={COLOR.GREY}
			className={className}
		>
			{tCommonUi(textKey)}
		</ChipsV2>
	) : null;

	return (
		<DashCard className={className}>
			<DashCard.Top textVariant="sans/14/600" icon="verified-v3">
				{tMipManage('investors.details.dashcards.kyc.title')}
			</DashCard.Top>
			<DashCard.Divider />
			<DashCard.Middle
				content={contentArray}
				status={middleRowStatusContent}
				button={middleRowButtonContent}
			/>
		</DashCard>
	);
};
